import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled, { keyframes } from 'styled-components';
import { Portal } from '@codex-brightworld/gadgets';
import { toggleOverflow, useEscape } from '@codex-brightworld/helpers';
import Close from '../Close';

const showPopup = keyframes`
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1020;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow-y: auto;
  ${props => props.theme.fonts.default};
  width: 100%;
  height: 100%;
  animation: ${showPopup} 0.2s ease;
`;

const Wrapper = styled.div`
  max-width: 600px;
  border-radius: 10px;
  background-color: ${props => props.theme.colors.neutral.white};
  box-shadow: 0 2px 6px 0 rgba(33, 48, 56, 0.2);

  @media ${props => props.theme.breakpoints.tablet} {
    max-width: 500px;
  }

  @media ${props => props.theme.breakpoints.mobile} {
    max-width: 350px;
    width: 100%;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  color: ${props => props.theme.colors.neutral.CurrysBlack};
  font-size: 20px;
  border-bottom: 1px solid ${props => props.theme.colors.neutral.grey[2]};
`;

const Body = styled.div`
  padding: 16px 24px;
`;

const Backdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1010;
  width: 100%;
  height: 100%;
  background-color: rgba(34, 34, 34, 0.6);
  animation: ${showPopup} 0.2s ease;
`;

const Popup = (props) => {
  const handleWrapperClick = useCallback(event => event.stopPropagation(), []);

  useEscape(props.onClose);

  useEffect(() => {
    toggleOverflow('body', true);

    return () => toggleOverflow('body', false);
  }, []);

  return (
    <Portal domNode={document.body}>
      <Backdrop />
      <Overlay
        className={`dc-popup-overlay ${props.optionalClasses} ${props.className}`}
        onClick={props.onClose}
        data-qa={props.qa}
      >
        {props.renderWrapper(Wrapper, { onClick: handleWrapperClick }, (
          <>
            {!props.hideHeaderBar && (
              <Header>
                <div>{props.title}</div>
                <Close onClose={props.onClose} />
              </Header>
            )}
            <Body>
              {props.children}
            </Body>
          </>
        ))}
      </Overlay>
    </Portal>
  );
};

Popup.propTypes = {
  children: PropTypes.node.isRequired,
  title: PropTypes.string,
  onClose: PropTypes.func,
  hideHeaderBar: PropTypes.bool,
  optionalClasses: PropTypes.string,
  qa: PropTypes.string,
  renderWrapper: PropTypes.func
};

Popup.defaultProps = {
  title: '',
  onClose: undefined,
  hideHeaderBar: false,
  optionalClasses: '',
  qa: undefined,
  renderWrapper: (DefaultWrapper, props, children) => (
    <DefaultWrapper {...props}>{children}</DefaultWrapper>
  )
};

/** @component */
export default Popup;
